import { useEffect, useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ModalPortal } from '../../../../components/modal-portal/ModalPortal';
import { useViewState } from '../../hooks/ViewStateContext';
import styles from './ViewStateSelector.module.scss';
import { ViewStateRow } from './ViewStateRow';
import Switch from '../../../../components/switch/Switch';
import { VIEW_STATE_TERMS } from '../../../../data/snapshot/dashboardStateSerialization';

interface ViewStateSelectorProps {
  close: () => void;
}

export function ViewStateSelector({ close }: ViewStateSelectorProps) {
  const { activeViewStateId, viewStates, isListLoading, listError, fetchViewStateList } = useViewState();
  const [searchText, setSearchText] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [showOwnOnly, setShowOwnOnly] = useState(false);

  // Fetch the list of view states when the component mounts.
  useEffect(() => {
    fetchViewStateList().catch(() => undefined);
  }, [fetchViewStateList]);

  // Filter the list of view states based on the search query.
  const query = searchText.trim().toLowerCase();
  const visibleViewStates = showOwnOnly ? viewStates.filter((viewState) => viewState.isOwn) : viewStates;
  const filteredViewStates = query
    ? visibleViewStates.filter((viewState) =>
        [viewState.name, viewState.description, viewState.createdBy].some((value) =>
          value?.toLowerCase().includes(query),
        ),
      )
    : visibleViewStates;

  // During a busy operation, prevent the modal from closing to avoid interrupting the ongoing process.
  const closeIfNotBusy = useCallback(() => {
    if (!isBusy) {
      close();
    }
  }, [isBusy, close]);

  return (
    <ModalPortal close={closeIfNotBusy}>
      <div className={styles.selector} role="dialog" aria-modal="true" aria-labelledby="view-state-title">
        <header className={styles.header}>
          <h2 id="view-state-title">Saved {VIEW_STATE_TERMS.plural}</h2>
        </header>
        <div className={styles.searchRow}>
          <label className={styles.searchLabel} htmlFor="view-state-search">
            Search {VIEW_STATE_TERMS.plural}:
          </label>
          <div className={styles.searchBox}>
            <FontAwesomeIcon icon={['fas', 'magnifying-glass']} aria-hidden="true" />
            <input
              id="view-state-search"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search by name, description, or author"
            />
          </div>
        </div>
        <div className={styles.filterRow}>
          <span id="view-state-own-filter">My saved {VIEW_STATE_TERMS.plural} only</span>
          <Switch isOn={showOwnOnly} onChanged={setShowOwnOnly} />
        </div>
        {isListLoading && <p role="status">Loading saved {VIEW_STATE_TERMS.plural}...</p>}
        {listError && <p role="alert">{listError}</p>}
        {!isListLoading && viewStates.length === 0 && (
          <p className={styles.emptyState}>No saved {VIEW_STATE_TERMS.plural} yet.</p>
        )}
        {!isListLoading && viewStates.length > 0 && filteredViewStates.length === 0 && (
          <p className={styles.emptyState}>No saved {VIEW_STATE_TERMS.plural} match your search.</p>
        )}
        <div className={styles.stateList}>
          {filteredViewStates.map((viewState) => (
            <ViewStateRow
              key={viewState.id}
              viewState={viewState}
              isActive={viewState.id === activeViewStateId}
              setIsBusy={setIsBusy}
              close={close}
            />
          ))}
        </div>
        <div className={styles.buttonSection}>
          <button type="button" onClick={closeIfNotBusy} disabled={isBusy}>
            {isBusy ? 'Loading...' : 'Close'}
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}
